import React, { useState, useEffect } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import MixLogo from "../../assets/images/logo.svg";
import { API_BASE_URL } from "../../config";
import {
  encryptString,
  // createSession,
  getAbhaProfile,
} from "../../services/apiUtils";
import { ToastContainer, toast } from "react-toastify";
import Loader from "../../components/common/Loader";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const FindABHA = () => {
  const navigate = useNavigate();

  // Step: "mobile" | "select" | "otp"
  const [step, setStep] = useState("mobile");

  // Step 1 – Mobile search
  const [mobile, setMobile] = useState("");
  const [formattedMobile, setFormattedMobile] = useState("");
  const [showMobile, setShowMobile] = useState(false);
  const [mobileError, setMobileError] = useState("");

  // Captcha
  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaSessionId, setCaptchaSessionId] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaError, setCaptchaError] = useState("");

  // Step 2 – ABHA list
  const [abhaList, setAbhaList] = useState([]);
  const [txnId, setTxnId] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Step 3 – OTP
  const [otpTxnId, setOtpTxnId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(60);
  const [resendCount, setResendCount] = useState(0); // 0 = initial sent, max 2 more

  const [isPageLoading, setIsPageLoading] = useState(false);

  // ─── Generate captcha on mount ─────────────────────────────────────
  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/abha/captcha/generate`);
      if (response.data.success) {
        setCaptchaSessionId(response.data.data.sessionId);
        setCaptchaImage(response.data.data.captchaImage);
        setCaptchaInput("");
        setCaptchaError("");
      } else {
        toast.error("Failed to generate captcha. Please try again.");
      }
    } catch (error) {
      console.error("Error generating captcha:", error);
      toast.error("Failed to generate captcha. Please try again.");
    }
  };

  // ─── Timer countdown ───────────────────────────────────────────────
  useEffect(() => {
    if (step !== "otp") return;
    if (resendTimer <= 0) return;
    const timer = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendTimer, step]);

  // ─── Mobile input handler (mask/unmask) ────────────────────────────
  const handleMobileChange = (e) => {
    const inputValue = e.target.value;

    if (showMobile) {
      const numeric = inputValue.replace(/[^0-9]/g, "").slice(0, 10);
      setMobile(numeric);
      setFormattedMobile(numeric);
      setMobileError(
        numeric.length > 0 && numeric.length < 10
          ? "Mobile number must be 10 digits"
          : "",
      );
    } else {
      const currentLen = formattedMobile.length;
      const newLen = inputValue.length;
      if (newLen > currentLen) {
        const newDigit = inputValue.replace(/[^0-9]/g, "").slice(-1);
        if (newDigit && mobile.length < 10) {
          const updated = mobile + newDigit;
          setMobile(updated);
          setFormattedMobile(updated.replace(/./g, "*"));
          setMobileError(
            updated.length < 10 ? "Mobile number must be 10 digits" : "",
          );
        }
      } else if (newLen < currentLen) {
        const updated = mobile.slice(0, -1);
        setMobile(updated);
        setFormattedMobile(updated.replace(/./g, "*"));
        setMobileError(
          updated.length > 0 && updated.length < 10
            ? "Mobile number must be 10 digits"
            : "",
        );
      }
    }
  };

  const toggleMobileVisibility = () => {
    setShowMobile((prev) => {
      if (!prev && mobile) setFormattedMobile(mobile);
      else if (mobile) setFormattedMobile(mobile.replace(/./g, "*"));
      return !prev;
    });
  };

  // ─── Step 1: Find ABHA by mobile ──────────────────────────────────
  const handleFindAbha = async () => {
    if (!mobile || mobile.length !== 10) {
      setMobileError("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!captchaInput || captchaInput.trim() === "") {
      setCaptchaError("Please enter the captcha");
      toast.error("Please enter the captcha");
      return;
    }

    setIsPageLoading(true);
    try {
      // Validate captcha first
      const captchaResponse = await axios.post(
        `${API_BASE_URL}/abha/captcha/validate`,
        {
          sessionId: captchaSessionId,
          captcha: captchaInput,
        },
      );
      if (!captchaResponse.data.success || !captchaResponse.data.valid) {
        setCaptchaError("Invalid captcha. Please try again.");
        toast.error("Invalid captcha. Please try again.");
        generateCaptcha();
        setIsPageLoading(false);
        return;
      }

      const encrypted = await encryptString(mobile);
      if (!encrypted) {
        toast.error("Encryption failed. Please try again.");
        setIsPageLoading(false);
        return;
      }

      // const sessionResponse = await createSession();
      // if (!sessionResponse?.accessToken) {
      //   toast.error("Session creation failed. Please try again.");
      //   setIsPageLoading(false);
      //   return;
      // }

      const response = await axios.post(
        `${API_BASE_URL}/abha/profile/index/find`,
        { loginId: encrypted },
        {
          headers: {
            "Content-Type": "application/json",
            // accesstoken: sessionResponse.accessToken,
          },
        },
      );

      const dtResponse = response.data;
      if (dtResponse.success && dtResponse.data?.length > 0) {
        const record = dtResponse.data[0];
        setTxnId(record.txnId);
        setAbhaList(record.ABHA || []);
        setStep("select");
      } else {
        toast.error(
          dtResponse.message ||
            "No ABHA accounts found for this mobile number.",
        );
      }
    } catch (error) {
      generateCaptcha();
      const errorMsg =
        error.response?.data?.error?.error?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error?.mobile ||
        error.response?.data?.message ||
        "Failed to find ABHA. Please try again.";
      toast.error(errorMsg);
    } finally {
      setIsPageLoading(false);
    }
  };

  // ─── Step 2: Send OTP for selected index ──────────────────────────
  const handleSelectAbha = async (abha) => {
    setSelectedIndex(abha);
    setIsPageLoading(true);
    try {
      // const sessionResponse = await createSession();
      // if (!sessionResponse?.accessToken) {
      //   toast.error("Session creation failed. Please try again.");
      //   setIsPageLoading(false);
      //   return;
      // }

      const encryptedIndex = await encryptString(String(abha.index));
      if (!encryptedIndex) {
        toast.error("Encryption failed. Please try again.");
        setIsPageLoading(false);
        setSelectedIndex(null);
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/abha/profile/index/send-otp`,
        { txnId: txnId, loginId: encryptedIndex },
        {
          headers: {
            "Content-Type": "application/json",
            // accesstoken: sessionResponse.accessToken,
          },
        },
      );

      const dtResponse = response.data;
      if (dtResponse.success) {
        const newTxnId = dtResponse.data?.data?.txnId;
        const message =
          dtResponse.data?.data?.message || "OTP sent successfully";
        setOtpTxnId(newTxnId);
        setResendTimer(60);
        setResendCount(0);
        setOtp("");
        setOtpError("");
        setStep("otp");
        toast.success(message);
      } else {
        toast.error(
          dtResponse.message || "Failed to send OTP. Please try again.",
        );
        setSelectedIndex(null);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to send OTP. Please try again.",
      );
      setSelectedIndex(null);
    } finally {
      setIsPageLoading(false);
    }
  };

  // ─── Step 3: Resend OTP ───────────────────────────────────────────
  const handleResendOtp = async () => {
    if (resendCount >= 2) {
      toast.error("Maximum resend attempts reached.");
      return;
    }

    setIsPageLoading(true);
    try {
      // const sessionResponse = await createSession();
      // if (!sessionResponse?.accessToken) {
      //   toast.error("Session creation failed. Please try again.");
      //   setIsPageLoading(false);
      //   return;
      // }

      const encryptedIndex = await encryptString(String(selectedIndex.index));
      if (!encryptedIndex) {
        toast.error("Encryption failed. Please try again.");
        setIsPageLoading(false);
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/abha/profile/index/send-otp`,
        { txnId: txnId, loginId: encryptedIndex },
        {
          headers: {
            "Content-Type": "application/json",
            // accesstoken: sessionResponse.accessToken,
          },
        },
      );

      const dtResponse = response.data;
      if (dtResponse.success) {
        const newTxnId = dtResponse.data?.data?.txnId;
        const message =
          dtResponse.data?.data?.message || "OTP resent successfully";
        setOtpTxnId(newTxnId);
        setResendTimer(60);
        setResendCount((prev) => prev + 1);
        setOtp("");
        setOtpError("");
        toast.success(message);
      } else {
        toast.error(dtResponse.message || "Failed to resend OTP.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to resend OTP. Please try again.",
      );
    } finally {
      setIsPageLoading(false);
    }
  };

  // ─── Step 3: Verify OTP ───────────────────────────────────────────
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setIsPageLoading(true);
    try {
      const encryptedOtp = await encryptString(otp);
      if (!encryptedOtp) {
        toast.error("Encryption failed. Please try again.");
        setIsPageLoading(false);
        return;
      }

      // const sessionResponse = await createSession();
      // if (!sessionResponse?.accessToken) {
      //   toast.error("Session creation failed. Please try again.");
      //   setIsPageLoading(false);
      //   return;
      // }

      const response = await axios.post(
        `${API_BASE_URL}/abha/profile/index/verify-otp`,
        { txnId: otpTxnId, otp: encryptedOtp },
        {
          headers: {
            "Content-Type": "application/json",
            // accesstoken: sessionResponse.accessToken,
          },
        },
      );

      const dtResponse = response.data;
      if (dtResponse.success && dtResponse.data?.authResult === "success") {
        // localStorage.setItem("x_token", xToken);
        localStorage.setItem("loginMethod", "mobile");

        toast.success(dtResponse.data.message || "OTP verified successfully");

        // Fetch full ABHA profile
        const abhaProfileData = await getAbhaProfile();

        if (abhaProfileData?.success) {
          localStorage.setItem(
            "orignalBeneficiaryData",
            JSON.stringify(abhaProfileData.data),
          );
          localStorage.setItem(
            "abhaProfileData",
            JSON.stringify(abhaProfileData),
          );
        } else {
          // Store what we have from verify response
          if (dtResponse.data.accounts?.length > 0) {
            localStorage.setItem(
              "orignalBeneficiaryData",
              JSON.stringify(dtResponse.data.accounts[0]),
            );
          }
        }

        navigate("/abha-Card");
      } else {
        const errMsg =
          dtResponse.data?.message || "Invalid OTP. Please try again.";
        setOtpError(errMsg);
        toast.error(errMsg);
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Failed to verify OTP. Please try again.";
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setIsPageLoading(false);
    }
  };

  // ─── Back navigation ──────────────────────────────────────────────
  const handleBack = () => {
    if (step === "otp") {
      setStep("select");
      setSelectedIndex(null);
      setOtp("");
      setOtpError("");
    } else if (step === "select") {
      setStep("mobile");
      setAbhaList([]);
      setTxnId("");
    } else {
      navigate(-1);
    }
  };

  // ─── OTP input handler ────────────────────────────────────────────
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    setOtp(value);
    if (value.length === 0) setOtpError("OTP is required");
    else if (value.length < 6) setOtpError("OTP must be 6 digits");
    else setOtpError("");
  };

  // ─── Render ───────────────────────────────────────────────────────
  return (
    <>
      <div className="mainFlow">
        <Navbar />
        <ToastContainer />
        {isPageLoading && <Loader />}

        <div className="container">
          <div className="pranHealtnMainUserFlow">
            <div className="abha-paranLogo">
              <img src={MixLogo} alt="Logo" />
            </div>

            <div className="userFlowAdharVer pre-create-ABHA">
              <div className="backArrow" onClick={handleBack}>
                <i className="material-icons">keyboard_backspace</i>
              </div>

              {/* ── Step 1: Enter Mobile ── */}
              {step === "mobile" && (
                <>
                  <div className="flowTitle">
                    <h4>Find ABHA</h4>
                    <p>
                      Enter your registered mobile number to find your ABHA
                      account
                    </p>
                  </div>

                  <div className="flow-feilds">
                    <div className="feilds">
                      <label>
                        Mobile Number <span style={{ color: "red" }}>*</span>
                      </label>
                      <div style={{ position: "relative", width: "100%" }}>
                        <input
                          type="text"
                          className="filds-form"
                          placeholder="Enter 10 digit mobile number"
                          value={mobile ? "+91 " + formattedMobile : ""}
                          autoComplete="off"
                          style={{ paddingRight: "40px", width: "100%" }}
                          onChange={(e) => {
                            const value = e.target.value
                              .replace("+91", "")
                              .trim();
                            handleMobileChange({ target: { value } });
                          }}
                        />
                        <i
                          className="material-icons"
                          onClick={toggleMobileVisibility}
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            fontSize: "20px",
                          }}
                        >
                          {showMobile ? "visibility" : "visibility_off"}
                        </i>
                      </div>
                      {mobileError && (
                        <span
                          style={{
                            color: "#dc3545",
                            fontSize: "12px",
                            marginTop: "5px",
                            display: "block",
                          }}
                        >
                          {mobileError}
                        </span>
                      )}
                    </div>

                    <div className="feilds" style={{ marginTop: "15px" }}>
                      <label>
                        Captcha <span style={{ color: "red" }}>*</span>
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          marginBottom: "10px",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: "#f0f0f0",
                            padding: "5px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                            flex: 1,
                            textAlign: "center",
                            userSelect: "none",
                            minHeight: "60px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {captchaImage ? (
                            <img
                              src={captchaImage}
                              alt="Captcha"
                              style={{ maxWidth: "100%", height: "auto" }}
                            />
                          ) : (
                            <span style={{ color: "#999" }}>
                              Loading captcha...
                            </span>
                          )}
                        </div>
                        <i
                          className="material-icons"
                          onClick={generateCaptcha}
                          style={{ cursor: "pointer", fontSize: "24px" }}
                          title="Refresh Captcha"
                        >
                          refresh
                        </i>
                      </div>
                      <input
                        type="text"
                        className="filds-form"
                        placeholder="Enter captcha text"
                        value={captchaInput}
                        onChange={(e) => {
                          setCaptchaInput(e.target.value);
                          setCaptchaError("");
                        }}
                        autoComplete="off"
                      />
                      {captchaError && (
                        <span
                          style={{
                            color: "#dc3545",
                            fontSize: "12px",
                            marginTop: "5px",
                            display: "block",
                          }}
                        >
                          {captchaError}
                        </span>
                      )}
                    </div>

                    <div className="feilds feildsBtn mt-5">
                      <button
                        className="custBtn btnCancel"
                        onClick={handleBack}
                      >
                        Back
                      </button>
                      <button
                        className={`custBtn ${mobile.length === 10 && captchaInput.length > 0 ? "btnSuccess" : "disabled"}`}
                        disabled={
                          mobile.length !== 10 || captchaInput.length === 0
                        }
                        style={{
                          opacity:
                            mobile.length !== 10 || captchaInput.length === 0
                              ? 0.5
                              : 1,
                          cursor:
                            mobile.length !== 10 || captchaInput.length === 0
                              ? "not-allowed"
                              : "pointer",
                        }}
                        onClick={handleFindAbha}
                      >
                        Search <i className="material-icons">search</i>
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* ── Step 2: Select ABHA ── */}
              {step === "select" && (
                <>
                  <div className="flowTitle">
                    <h4>Select ABHA Account</h4>
                    <p>
                      We found the following ABHA account(s) linked to your
                      mobile number. Select one to continue.
                    </p>
                  </div>

                  <div className="flow-feilds">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: "16px",
                        marginBottom: "30px",
                      }}
                    >
                      {abhaList.map((abha) => (
                        <div
                          key={abha.index}
                          style={{
                            border: "1px solid #ddd",
                            borderRadius: "12px",
                            padding: "20px",
                            backgroundColor: "#fff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              marginBottom: "12px",
                            }}
                          >
                            <div
                              style={{
                                width: "44px",
                                height: "44px",
                                borderRadius: "50%",
                                backgroundColor: "#e3f2fd",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <i
                                className="material-icons"
                                style={{ color: "#1976d2", fontSize: "22px" }}
                              >
                                person
                              </i>
                            </div>
                            <div>
                              <p
                                style={{
                                  margin: 0,
                                  fontWeight: "600",
                                  fontSize: "15px",
                                  color: "#222",
                                }}
                              >
                                {abha.name}
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "12px",
                                  color: "#888",
                                }}
                              >
                                {abha.gender === "M"
                                  ? "Male"
                                  : abha.gender === "F"
                                    ? "Female"
                                    : abha.gender}
                              </p>
                            </div>
                          </div>

                          <div style={{ marginBottom: "8px" }}>
                            <p
                              style={{
                                margin: "0 0 2px",
                                fontSize: "11px",
                                color: "#999",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              ABHA Number
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontWeight: "500",
                                fontSize: "13px",
                                color: "#333",
                                fontFamily: "monospace",
                              }}
                            >
                              {abha.ABHANumber}
                            </p>
                          </div>

                          {abha.kycVerified === "true" && (
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                backgroundColor: "#e8f5e9",
                                color: "#2e7d32",
                                fontSize: "11px",
                                fontWeight: "600",
                                padding: "3px 8px",
                                borderRadius: "20px",
                                marginBottom: "14px",
                              }}
                            >
                              <i
                                className="material-icons"
                                style={{ fontSize: "13px" }}
                              >
                                verified
                              </i>
                              KYC Verified
                            </div>
                          )}

                          <button
                            className="custBtn btnSuccess"
                            style={{ width: "100%", marginTop: "4px" }}
                            onClick={() => handleSelectAbha(abha)}
                          >
                            Select{" "}
                            <i className="material-icons">arrow_right_alt</i>
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="feilds feildsBtn">
                      <button
                        className="custBtn btnCancel"
                        onClick={handleBack}
                      >
                        Back
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* ── Step 3: Enter OTP ── */}
              {step === "otp" && (
                <>
                  <div className="flowTitle">
                    <h4>Verify OTP</h4>
                    <p>
                      An OTP has been sent to the mobile number linked with{" "}
                      <strong>{selectedIndex?.name}</strong>
                    </p>
                  </div>

                  <div className="flow-feilds">
                    <div className="feilds">
                      <label>
                        Enter OTP <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type="text"
                        className="filds-form"
                        placeholder="Enter 6 digit OTP"
                        value={otp}
                        maxLength={6}
                        autoComplete="off"
                        onChange={handleOtpChange}
                      />
                      {otpError && (
                        <span
                          style={{
                            color: "#dc3545",
                            fontSize: "12px",
                            marginTop: "5px",
                            display: "block",
                          }}
                        >
                          {otpError}
                        </span>
                      )}

                      <div
                        style={{
                          marginTop: "10px",
                          fontSize: "13px",
                        }}
                      >
                        {resendTimer > 0 ? (
                          <span style={{ color: "#666" }}>
                            {`00:${resendTimer < 10 ? "0" + resendTimer : resendTimer}`}{" "}
                            Resend OTP ({2 - resendCount}{" "}
                            {2 - resendCount === 1 ? "attempt" : "attempts"}{" "}
                            left)
                          </span>
                        ) : resendCount < 2 ? (
                          <button
                            onClick={handleResendOtp}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#007bff",
                              cursor: "pointer",
                              textDecoration: "underline",
                              padding: 0,
                              fontSize: "13px",
                            }}
                          >
                            Resend OTP ({2 - resendCount}{" "}
                            {2 - resendCount === 1 ? "attempt" : "attempts"}{" "}
                            left)
                          </button>
                        ) : (
                          <span style={{ color: "#dc3545" }}>
                            Maximum resend attempts reached
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="feilds feildsBtn mt-5">
                      <button
                        className="custBtn btnCancel"
                        onClick={handleBack}
                      >
                        Back
                      </button>
                      <button
                        className={`custBtn ${otp.length === 6 ? "btnSuccess" : "disabled"}`}
                        disabled={otp.length !== 6}
                        style={{
                          opacity: otp.length !== 6 ? 0.5 : 1,
                          cursor: otp.length !== 6 ? "not-allowed" : "pointer",
                        }}
                        onClick={handleVerifyOtp}
                      >
                        Verify <i className="material-icons">arrow_right_alt</i>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FindABHA;
